import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFile,
  faFilePdf,
  faFileExcel,
  faFileWord,
  faFileImage,
  faDownload,
  faFolderClosed,
  faFolderOpen,
  faSearch,
  faCheck,
  faSpinner,
  faExclamationTriangle,
  faFilter,
  faSort,
  faFileDownload,
  faChevronDown,
  faChevronUp,
  faEye,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { createAndDownloadZip, downloadSingleFile } from "../utils/JSZipUtil";
import useDocuments from "../utils/useDocuments";
import DocumentStats from "./DocumentStats";

const DocumentBrowser = ({ item }) => {
  const [previewFile, setPreviewFile] = useState(null);

  // Use our custom hook to manage documents
  const {
    documents,
    loading,
    error,
    searchTerm,
    expandedGroups,
    selectedFiles,
    groupFilter,
    sortField,
    sortDirection,
    prefixes,
    filteredAndSortedGroups,
    documentStats,

    setSearchTerm,
    setGroupFilter,
    setSortField,
    setSortDirection,

    toggleFileSelection,
    isFileSelected,
    toggleGroup,
  } = useDocuments(item?.header?.pedimento);

  // Handle file download
  const handleFileDownload = (file) => {
    downloadSingleFile(file);
  };

  // Download group of files
  const downloadGroup = async (files, prefix) => {
    const success = await createAndDownloadZip(files, `${prefix}_Documents`);
    if (!success) {
      // Fallback if zip creation fails
      alert("Could not create zip file. Try downloading files individually.");
    }
  };

  // Download selected files
  const downloadSelected = async () => {
    if (selectedFiles.length === 0) return;

    const success = await createAndDownloadZip(
      selectedFiles,
      "Selected_Documents"
    );
    if (!success) {
      // Fallback if zip creation fails
      alert("Could not create zip file. Try downloading files individually.");
    }
  };

  // Download all files
  const downloadAll = async () => {
    if (documents.length === 0) return;

    try {
      // Organize files by prefix for better structure in the zip
      const organizedFiles = documents.map((file) => ({
        ...file,
        Note: `${file.Prefijo || "Other"}/${file.Note || "file"}`,
      }));

      const success = await createAndDownloadZip(
        organizedFiles,
        "All_Documents"
      );
      if (!success) {
        throw new Error("Failed to create zip");
      }
    } catch (error) {
      alert("Could not create zip file. Try downloading files individually.");
    }
  };

  // Get appropriate file icon
  const getFileIcon = (extension) => {
    const ext = (extension || "").toLowerCase();

    if (["jpg", "jpeg", "png", "gif", "bmp", "svg"].includes(ext)) {
      return faFileImage;
    } else if (ext === "pdf") {
      return faFilePdf;
    } else if (["xls", "xlsx", "csv"].includes(ext)) {
      return faFileExcel;
    } else if (["doc", "docx", "rtf"].includes(ext)) {
      return faFileWord;
    } else {
      return faFile;
    }
  };

  // Preview file
  const openFilePreview = (file) => {
    setPreviewFile(file);
  };

  // Close preview
  const closePreview = () => {
    setPreviewFile(null);
  };

  // Render file preview modal
  const renderPreviewModal = () => {
    if (!previewFile) return null;

    const fileExt = (previewFile.Ext || "").toLowerCase();
    const isImage = ["jpg", "jpeg", "png", "gif", "bmp"].includes(fileExt);
    const isPdf = fileExt === "pdf";

    return (
      <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-75 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg w-4/5 max-w-6xl max-h-[90vh] flex flex-col">
          <div className="flex justify-between items-center bg-blue-900 text-white p-3 rounded-t-lg">
            <h3 className="text-lg font-medium">
              {previewFile.Note || "Document Preview"}
            </h3>
            <div className="flex space-x-3">
              <button
                onClick={() => handleFileDownload(previewFile)}
                className="text-blue-100 hover:text-white"
                title="Download"
              >
                <FontAwesomeIcon icon={faDownload} />
              </button>
              <button
                onClick={closePreview}
                className="text-blue-100 hover:text-white"
                title="Close"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-4 bg-gray-100 flex items-center justify-center">
            {isImage ? (
              <img
                src={`data:image/${fileExt};base64,${previewFile.DocBytes}`}
                alt={previewFile.Note || "Document preview"}
                className="max-w-full max-h-[70vh] object-contain"
              />
            ) : isPdf ? (
              <iframe
                src={`data:application/pdf;base64,${previewFile.DocBytes}`}
                className="w-full h-[70vh]"
                title={previewFile.Note || "PDF preview"}
              ></iframe>
            ) : (
              <div className="text-center p-10">
                <FontAwesomeIcon
                  icon={getFileIcon(previewFile.Ext)}
                  className="text-6xl text-gray-400 mb-4"
                />
                <p className="text-lg text-gray-700">
                  Preview not available for this file type.
                </p>
                <button
                  onClick={() => handleFileDownload(previewFile)}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-150"
                >
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Download File
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FontAwesomeIcon
          icon={faSpinner}
          className="text-blue-500 text-4xl animate-spin mb-4"
        />
        <p className="text-gray-600">Loading documents...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="text-red-500 text-4xl mb-4"
        />
        <p className="text-red-600 font-medium mb-2">Error loading documents</p>
        <p className="text-gray-600">{error}</p>
      </div>
    );
  }

  // Render empty state
  if (documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <FontAwesomeIcon
          icon={faFile}
          className="text-gray-400 text-4xl mb-4"
        />
        <p className="text-gray-600">
          No documents available for this shipment
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Document Stats */}
      <DocumentStats stats={documentStats} />

      {/* Control bar - UPDATED SECTION */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <div className="flex flex-col gap-3">
          {/* Top row with search and download all */}
          <div className="flex justify-between items-center">
            <div className="relative w-1/2 md:w-1/3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
              </div>
              <input
                type="text"
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-blue-500 focus:border-blue-500"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button
              onClick={downloadAll}
              className="px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-700 transition-colors duration-150 flex items-center"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              Download All
            </button>
          </div>

          {/* Second row with filters and selected downloads */}
          <div className="flex flex-wrap justify-between gap-2">
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <select
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-300 rounded-md appearance-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Categories</option>
                  {prefixes.map((prefix) => (
                    <option key={prefix} value={prefix}>
                      {prefix}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-gray-400"
                  />
                </div>
              </div>

              <div className="relative">
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                  className="pl-3 pr-8 py-2 border border-gray-300 rounded-md appearance-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="UploadDate">Upload Date</option>
                  <option value="Note">File Name</option>
                  <option value="Ext">File Type</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className="text-gray-400"
                  />
                </div>
              </div>

              <button
                onClick={() =>
                  setSortDirection(sortDirection === "asc" ? "desc" : "asc")
                }
                className="p-2 border border-gray-300 rounded-md focus:outline-none hover:bg-gray-100"
                title={`Sort ${
                  sortDirection === "asc" ? "Descending" : "Ascending"
                }`}
              >
                <FontAwesomeIcon icon={faSort} className="text-gray-600" />
                <FontAwesomeIcon
                  icon={sortDirection === "asc" ? faChevronUp : faChevronDown}
                  className="text-gray-600 ml-1 text-xs"
                />
              </button>
            </div>

            {selectedFiles.length > 0 && (
              <button
                onClick={downloadSelected}
                className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors duration-150 flex items-center"
              >
                <FontAwesomeIcon icon={faFileDownload} className="mr-2" />
                <span>Download Selected ({selectedFiles.length})</span>
              </button>
            )}
          </div>
        </div>
      </div>
      {/* END OF UPDATED SECTION */}

      {/* Document groups */}
      <div className="p-4 space-y-4">
        {filteredAndSortedGroups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No documents match your search criteria
          </div>
        ) : (
          filteredAndSortedGroups.map(([prefix, files]) => (
            <div
              key={prefix}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Group header */}
              <div
                className={`px-4 py-3 flex justify-between items-center cursor-pointer ${
                  expandedGroups[prefix] ? "bg-blue-50" : "bg-gray-50"
                }`}
                onClick={() => toggleGroup(prefix)}
              >
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon
                    icon={
                      expandedGroups[prefix] ? faFolderOpen : faFolderClosed
                    }
                    className={`${
                      expandedGroups[prefix] ? "text-blue-500" : "text-gray-500"
                    }`}
                  />
                  <span className="font-medium">{prefix}</span>
                  <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full">
                    {files.length} {files.length === 1 ? "file" : "files"}
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadGroup(files, prefix);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                    title={`Download all ${prefix} files`}
                  >
                    <FontAwesomeIcon icon={faDownload} className="mr-1" />
                    <span className="hidden sm:inline">Download Group</span>
                  </button>
                  <FontAwesomeIcon
                    icon={expandedGroups[prefix] ? faChevronUp : faChevronDown}
                    className="text-gray-500"
                  />
                </div>
              </div>

              {/* Group content */}
              {expandedGroups[prefix] && (
                <div className="p-4 bg-white">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {files.map((file, fileIndex) => {
                      const fileExt = (file.Ext || "").toLowerCase();
                      const isImage = [
                        "jpg",
                        "jpeg",
                        "png",
                        "gif",
                        "bmp",
                      ].includes(fileExt);
                      const isPdf = fileExt === "pdf";
                      const isPreviewable = isImage || isPdf;

                      return (
                        <div
                          key={fileIndex}
                          className={`border rounded-lg overflow-hidden hover:shadow-md transition duration-150 ${
                            isFileSelected(file)
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200"
                          }`}
                        >
                          <div className="p-3">
                            <div className="flex justify-between items-start">
                              <div className="flex-1 min-w-0">
                                {/* File preview */}
                                <div className="mb-3 h-28 flex items-center justify-center overflow-hidden bg-gray-50 rounded-md">
                                  {isImage ? (
                                    <img
                                      src={`data:image/${fileExt};base64,${file.DocBytes}`}
                                      alt={file.Note || "Image"}
                                      className="h-full object-contain"
                                    />
                                  ) : (
                                    <FontAwesomeIcon
                                      icon={getFileIcon(file.Ext)}
                                      className="text-4xl text-gray-500"
                                    />
                                  )}
                                </div>

                                {/* File info */}
                                <div>
                                  <div
                                    className="truncate font-medium text-sm"
                                    title={file.Note || "Unnamed file"}
                                  >
                                    {file.Note || "Unnamed file"}
                                  </div>
                                  <div className="mt-1 flex items-center justify-between">
                                    <span className="text-xs text-gray-500">
                                      {file.Ext
                                        ? `.${file.Ext}`
                                        : "Unknown type"}
                                    </span>
                                    {file.UploadDate && (
                                      <span className="text-xs text-gray-500">
                                        {new Date(
                                          file.UploadDate
                                        ).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Selection checkbox */}
                              <button
                                onClick={() => toggleFileSelection(file)}
                                className={`h-5 w-5 rounded-md flex items-center justify-center ${
                                  isFileSelected(file)
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "border border-gray-300 text-transparent"
                                }`}
                              >
                                {isFileSelected(file) && (
                                  <FontAwesomeIcon
                                    icon={faCheck}
                                    className="text-xs"
                                  />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex border-t border-gray-100 divide-x divide-gray-100">
                            {isPreviewable && (
                              <button
                                onClick={() => openFilePreview(file)}
                                className="flex-1 py-2 text-xs text-center text-blue-600 hover:bg-blue-50 transition-colors duration-150"
                              >
                                <FontAwesomeIcon
                                  icon={faEye}
                                  className="mr-1"
                                />
                                Preview
                              </button>
                            )}
                            <button
                              onClick={() => handleFileDownload(file)}
                              className={`${
                                isPreviewable ? "flex-1" : "w-full"
                              } py-2 text-xs text-center text-blue-600 hover:bg-blue-50 transition-colors duration-150`}
                            >
                              <FontAwesomeIcon
                                icon={faDownload}
                                className="mr-1"
                              />
                              Download
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Preview modal */}
      {renderPreviewModal()}
    </div>
  );
};

export default DocumentBrowser;
